import './App.css';
import { Navigate, Route, Routes } from 'react-router-dom';
import React, { lazy, Suspense, useEffect } from 'react';
import * as Sentry from "@sentry/react";

// SCREENS
import Loading from './components/Loading';
import Dashboard from './pages/Dashboard';
import Integrations from './pages/Integrations';
import SignUpScreen from './pages/SignUp';
import CompanySettings from './pages/CompanySettings';
import Billing from './pages/CompanySettings/Billing';
import GeneralSettings from './pages/CompanySettings/GeneralSettings';
import Notifications from './pages/CompanySettings/Notifications';
import Plan from './pages/CompanySettings/Plan';
import Security from './pages/CompanySettings/Security';
import Teams from './pages/CompanySettings/Teams';
import FirstIntegrations from './pages/Integrations/firstIntegration';
import CreateProfileFirst from './pages/create-profile';

// COMPONENTS
import PublicRoute from './appLayout/publicLayout';
import PrivateRoute from './appLayout/privateLayout';

import 'react-toastify/dist/ReactToastify.css';
import ErrorPage from './pages/ErrorPage';
import { ViewRawDataUnauthorized } from './pages/Integrations/viewRawData';
import VerifyEmail from './pages/signupFlow/verifyemail';
import SetPassword from './pages/signupFlow/set-password';
import AboutYourCompany from './pages/signupFlow/aboutYourCompany';
import IntegrationPreference from './pages/signupFlow/integrationPreference';
import SelectGoals from './pages/signupFlow/selectGoals';
import SetUpConnection from './pages/signupFlow/setUpConnection';
import EnterReferralCode from './pages/signupFlow/enterReferralCode';
import MlTest from './pages/mlTest';
import IntentClassification from './pages/Testing/IntentClassification';
import QuestionTestingMeta from './pages/Testing/QuestionTestinMeta';
import ForgotPassword from './pages/Login/ForgotPassword';
import MetaTest from './pages/metaTest';
import Checkout from './pages/CompanySettings/checkout';
import GoogleAdsTest from './pages/googleadsTest';
// import ContactForm from './pages/contactus';
import CustomBogQueryTest from './pages/customBigQueryTest';
import GoogleAdsQueryValidator from './pages/googleAdsQueryValidator';
import { CloseIcon } from './components/Icons/iconImages';
import { GlobalToastContainer, toast } from './components/Toast/toast';
import DashboardLayout from './pages/Dashboard/DashboardLayout';
import useNetworkStatus from './Hooks/useNetworkStatus';
import { useSelector } from 'react-redux';
import { RootState } from './store';
import FeedbackHistory from './pages/CompanySettings/FeedbackHistory';
import Referrals from './pages/CompanySettings/Referrals';
import AuditLog from './pages/CompanySettings/AuditLog';
import { WorkspaceProvider } from './contexts/WorkspaceContext';
import { SidebarProvider } from './contexts/SidebarContext';
import { ReportIssue } from './pages/Support';

import SetupWorkspace from './pages/signupFlow/setupWorkspace';
import ComponentTestPlayground from './pages/Testing/ComponentTestPlayground';
import useSentryUser from './Hooks/useSentryUser';
import TestingIndex from './pages/Testing/TestingIndex';
import NetworkWatcher from './Hooks/NetworkWatcher';
import useIsInIframe from './Hooks/useIsInIframe';
import TestGdsQueryPage from './pages/testBulkGdsQuery';
import OogwaiProtectedRoute from './helper/OogwaiProtectedRoute';
import WidgetLogs from './pages/WidgetLogs';

const LoginScreen = lazy(() => import('./pages/Login'));
const ViewRawData = lazy(() => import('./pages/Integrations/viewRawData'));
const ViewCustomEvents = lazy(() => import('./pages/Integrations/viewCustomEvents'));
const ConversationScreen = lazy(() => import('./pages/Conversation'));

function App() {
  const [loading, setLoading] = React.useState(false);
  // const isOnline = useSelector((state:RootState) => state.static.isOnline);
  // const globalOnlineStatus = useNetworkStatus();
  const isInIframe = useIsInIframe();

  useSentryUser();

  const urlParams = new URLSearchParams(window.location.search);
  const basePath = urlParams.get("basepath");
  const stateParam = urlParams.get("state")?.split(':')[1]; // for GA
  // console.log("basePath", basePath, stateParam);

  if (loading) {
    return <Loading />;
  }

  return (
    <Sentry.ErrorBoundary
      showDialog
      fallback={({ error, componentStack, resetError }) => (
        <ErrorPage error={error} resetErrorBoundary={resetError} />
      )}
    >
      <main className={"h-full"}>
        <GlobalToastContainer />
        <NetworkWatcher/>
        <Suspense fallback={<Loading />}>
          <Routes>
            {/* <Route path="error" element={<ErrorPage />} /> */}
            <Route path="/shopify/token" element={
              <WorkspaceProvider>
                <SidebarProvider>
                  <Integrations />
                </SidebarProvider>
              </WorkspaceProvider>
            } />
            <Route
              path="shopify/view-raw-data"
              element={<ViewRawDataUnauthorized />}
            />
            <Route path="/shopify/*" element={
              <WorkspaceProvider>
                <SidebarProvider>
                  <Integrations />
                </SidebarProvider>
              </WorkspaceProvider>
            } />
            {/* <Route path="/demo" element={<ContactForm />} /> */}

              <Route path="/set-up-connection" element={
                <WorkspaceProvider>
                  <SetUpConnection />
                </WorkspaceProvider>
              } />
            <Route element={<PublicRoute />}>
              <Route path="/login" element={<LoginScreen />} />
              <Route path="/signup" element={<LoginScreen />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/enter-referral-code" element={<EnterReferralCode />} />
              <Route path="/set-password" element={<SetPassword />} />
              <Route path="/about-your-company" element={<AboutYourCompany />} />
              <Route path="/integration-pereference" element={<IntegrationPreference />} />
              <Route path="/select-goals" element={<SelectGoals />} />
              <Route path="/setup-workspace" element={<SetupWorkspace />} />
              {
                basePath === 'set-up-connection' && (
                  <>
                    <Route path="/oauth/google" element={
                      <WorkspaceProvider>
                        <SetUpConnection />
                      </WorkspaceProvider>
                    } />
                    <Route path="/oauth/youtube" element={
                      <WorkspaceProvider>
                        <SetUpConnection />
                      </WorkspaceProvider>
                    } />
                    <Route path="/oauth/google-ads" element={
                      <WorkspaceProvider>
                        <SetUpConnection />
                      </WorkspaceProvider>
                    } />
                    <Route path="/shopify/auth/callback" element={
                      <WorkspaceProvider>
                        <SetUpConnection />
                      </WorkspaceProvider>
                    } />
                    <Route path="/meta/auth" element={
                      <WorkspaceProvider>
                        <SetUpConnection />
                      </WorkspaceProvider>
                    } />
                  </>
                )
              }
            </Route>

            <Route element={
              <WorkspaceProvider>
                <SidebarProvider>
                  <PrivateRoute />
                </SidebarProvider>
              </WorkspaceProvider>
            }>
              <Route path="dashboard" element={<DashboardLayout pathName={"/dashboard"} />} />
              <Route path="conversation" element={<DashboardLayout pathName={"/conversation"} />} />
              <Route path="onboardingIntegration" element={<FirstIntegrations signup={false} />} />
              <Route path="create-profile" element={<CreateProfileFirst />} />
              <Route path="edit-profile/:id" element={<CreateProfileFirst />} />
              <Route path="/mnbvcxz-Test-zxcvbnm" element={<MlTest />} />
              <Route path="/test-meta-objects" element={<MetaTest />} />
              <Route path="/test-google-ads-object" element={<GoogleAdsTest />} />
              <Route path="/test-custom-big-query-object" element={<CustomBogQueryTest />} />
              <Route path="/test-google-ads-query-validator" element={<GoogleAdsQueryValidator />} />




              <Route path='/testing' element={<TestingIndex />} />
              <Route path='/testing/intent-classification' element={<IntentClassification />} />
              <Route path='/testing/question-testing-meta' element={<QuestionTestingMeta />} />
              <Route path='/testing/component-playground' element={<ComponentTestPlayground />} />
              <Route path='/test-gads-query' element={<OogwaiProtectedRoute><TestGdsQueryPage /></OogwaiProtectedRoute>} />
              <Route path='/widget-logs' element={<OogwaiProtectedRoute><WidgetLogs /></OogwaiProtectedRoute>} />


              {(basePath === "onboardingintegration" || stateParam === "onboardingintegration") ? (
                <>
                  <Route path="integrations" element={<FirstIntegrations signup={false} />} />
                  <Route path="oauth/google" element={<FirstIntegrations signup={false} />} />
                  <Route path="oauth/youtube" element={<FirstIntegrations signup={false} />} />
                  <Route path="/oauth/google-ads" element={<FirstIntegrations signup={false} />} />
                  <Route path="/shopify/auth/callback" element={<FirstIntegrations signup={false} />} />
                  <Route path="/meta/auth" element={<FirstIntegrations signup={false} />} />
                </>
              ) : (
                <>
                  <Route path="oauth/google" element={<Integrations />} />
                  <Route path="oauth/youtube" element={<Integrations />} />
                  <Route path="/oauth/google-ads" element={<Integrations />} />
                  <Route path="/shopify/auth/callback" element={<Integrations />} />
                  <Route path="/meta/auth" element={<Integrations />} />
                  <Route path="integrations" element={<Integrations />} />
                </>
              )}
              <Route
                path="integrations/view-raw-data/:integrationId"
                element={<ViewRawData />}
              />
              <Route
                path="integrations/custom-events-data/:integrationId"
                element={<ViewCustomEvents />}
              />
              <Route path="company-settings" element={<CompanySettings />}>
                <Route index element={<Navigate to="general" replace />} />
                <Route path="general" element={<GeneralSettings />} />
                <Route path="notifications" element={<Notifications />} />
                <Route path="security" element={<Security />} />
                <Route path="teams" element={<Teams />} />
                <Route path="plan" element={<Plan />} />
                <Route path="feedback-history" element={<FeedbackHistory />} />
                <Route path="referrals" element={<Referrals />} />
                <Route path="audit-log" element={<AuditLog />} />
                <Route path="plan/checkout" element={<Checkout />} />
                {/* <Route path="billing" element={<Billing />} /> */}
              </Route>
              <Route path="support/report-issue" element={<ReportIssue />} />
              <Route path="*" element={<Navigate to="/dashboard" />} />
            </Route>
            <Route index element={<Navigate to="/login" replace />} />
          </Routes >
        </Suspense>
      </main>
    </Sentry.ErrorBoundary>
  );
}


export default App;
